/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */

import {
  IAgentPlugin,
  IPluginMethodMap,
} from "@vlinder-web/plugin-agent-service-react";
import { VApplication } from "../../../application";
import { toHeaderCase } from "js-convert-case";
import config from "../config/featuresSection.config.json";
import moment from "moment";

export interface IFormatPolicyDetailMethods extends IPluginMethodMap {
  getFormatPolicyDetailSync(args: any): any;
}

const lookup = require("country-code-lookup");

export class FormatPolicyDetailService implements IAgentPlugin {
  readonly methods: IFormatPolicyDetailMethods;

  constructor() {
    this.methods = {
      getFormatPolicyDetailSync: this.getProps.bind(this),
    };
  }

  getProps(args: any) {
    return {
      policyId: args?.policyId,
      title: args?.policyNumber,
      premium: args?.premium,
      subTitle: args?.policyNumber,
      minDue: args?.minDue,
      statusComp:args?.status ? {
        title:`Current status: ${toHeaderCase(args?.status)}`,
        statusType:"success"
      }:null,
      listItems: this.getListItems(args),
      accordionItems:this.getAccordionItems(args),
      beneficiaryDetails: this.getBeneficiary(args?.benificiaryDetail),
      policyDetails: this.getPolicyDetails(args),
      useOfProceedDetails: this.getUseOfProceeds(args),
    };
  }

  private getAccordionItems(res:any){
    let items:any = [];
    let _policyHolder = this.getPolicyDetails(res);
    if (res?.description) {
      items.push({
          type: "text",
          title: "Description",
          "data": [
              res?.description
          ]
      })
    }
    if(_policyHolder){
      items?.push({
        type:"multi-col-list",
        numberOfColumns:"3",
        title:_policyHolder?.title,
        data:[..._policyHolder?.listItems]
      })
    }

    if(res?.purchasedBy){
      let _holder = this.getPolicyHolder(res?.purchasedBy);
      if(_holder){
        items?.push({
          type:"multi-col-list",
          numberOfColumns:"3",
          title:_holder?.title,
          data:[..._holder?.listItems]
        })
      }
    }

    if(res?.benificiaryDetail){
      let _temp = this.getBeneficiary(res?.benificiaryDetail);
      items.push({
        type:"multi-col-list",
        numberOfColumns:"3",
        title:_temp?.title,
        data:[..._temp?.listItems]
      });
    }

    let _useOfProceeds = this.getUseOfProceeds(res);
    if(_useOfProceeds){
      items.push({
        type:"check-list",
        title:_useOfProceeds?.title,
        data:[..._useOfProceeds?.listItems]
      })
    }
    return items;
  }

  private getListItems(res: any) {
    console.log("payments", res?.payments);
    let _payment =
      Array?.isArray(res?.payments) &&
      res?.payments?.map((item) => {
        return item?.status;
      });
    let _partialPayment =
      Array?.isArray(res?.payments) &&
      res?.payments?.find((item) => item?.status === "PARTIALLY_PAID");

    let _temp: any = [];
    _temp = config?.listItems?.map((ele: any) => {
      if (ele?.key === "policyName") {
        return {
          ...ele,
          subTitle: `Policy Option ${res?.productDefination}`,
        };
      }
      if (ele?.key === "coverage") {
        return {
          ...ele,
          subTitle: res?.coverage.replace(/^EC/, ''),
        };
      }
      if (ele?.key === "premium") {
        return {
          ...ele,
          subTitle: res?.premium.replace(/^EC/, ''),
        };
      }
      if (ele?.key === "country") {
        return {
          ...ele,
          subTitle: res?.countryToBeInsured
            ? "Florida"
            : "-",
          flag: "us",
        };
      }
    });
    if (
      Array.isArray(_payment) &&
      _payment?.includes("PARTIALLY_PAID") &&
      !_payment?.includes("FULLY_PAID")
    ) {
      config?.partialPayment.forEach((element) => {
        if (element?.key === "partialPaid") {
          _temp.push({
            ...element,
            subTitle: _partialPayment?.amount,
          });
        }
        if (element?.key === "premiumDue") {
          _temp.push({
            ...element,
            subTitle: `$${res?.minDue}`,
          });
        }
      });
    }
    if(res?.discountedPremium && res?.premium !== res?.discountedPremium){
      _temp.push({
        title:"Discounted Premium",
        subTitle:res?.discountedPremium.replace(/^EC/, ''),
        icon:config?.discountIcon
      })
    }
    return _temp;
  }
  private getBeneficiary(res: any) {
    let _beneficiary = {
      firstName: res?.firstName,
      lastName: res?.lastName,
      email: res?.email,
      phoneNumber: res?.mobileNo,
      streetAddress: res?.address?.addressLine1,
      city: res?.address?.city,
      country:
        res?.address?.country && res?.address?.country.length === 2
          ? lookup.byIso(`${res?.address?.country}`)?.country
          : "-",
    };
    let _temp = [];
    for (const [key, value] of Object.entries(_beneficiary)) {
      _temp.push({
        title: toHeaderCase(key),
        subTitle: value,
      });
    }
    return {
      title: config?.beneficiaryDetails?.title,
      listItems: _temp,
    };
  }

  private getPolicyHolder(data:any) {
    let _temp = [];
    _temp = config?.policyHolder?.map(ele=>{
      if(ele?.key === "address"){
        return({
          ...ele,
          subTitle: `${data[ele?.key]?.addressLine1}, ${data[ele?.key]?.city}, ${data[ele?.key]?.country && data[ele?.key]?.country.length ===2 ? lookup.byIso(`${data[ele?.key]?.country}`)?.country: "-"}`
        })
      }
      else if(ele?.key === "firstName" || ele?.key === "lastName"){
        return({
          ...ele,
          subTitle: toHeaderCase(data[ele?.key] ?? "-")
        })
      }
      else return({
        ...ele,
        subTitle:data[ele?.key]
      })
    });
    return {
      title: config?.purchaser?.title,
      listItems: _temp,
    };
  }

  private getPolicyDetails(res: any) {
    let _temp = [
      {
        title: "Policy Number",
        subTitle: res?.policyNumber,
      },
      {
        title: "Policy Option",
        subTitle: `Policy Option ${res?.productDefination}`,
      },
      {
        title: "Coverage Amount",
        subTitle: res?.coverage.replace(/^EC/, ''),
      },
      {
        title: "Annual Premium",
        subTitle: res?.premium.replace(/^EC/, ''),
      },
      {
        title: "Purchase on",
        subTitle: moment(res?.purchasedAt).format("DD/MM/YYYY"),
      },
      {
        title: "Policy Type",
        subTitle: toHeaderCase(res?.type ?? "-"),
      },
      {
        title: "insured Province",
        subTitle: res?.countryToBeInsured
          ? "Florida"
          : "-",
      },
    ];
    return {
      title: config?.policyDetails?.title,
      listItems: _temp,
    };
  }

  private getUseOfProceeds(data: any) {
    let _temp: any = [];
    Array.isArray(data?.useOfProceeds) &&
      data?.useOfProceeds?.forEach((element: any) => {
        if (element.selected) {
          _temp.push(toHeaderCase(element?.value ?? ""))
        }
    });

    return {
      title: config?.useOfProceedsDetail?.title,
      listItems: _temp,
    };
  }
}
